import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import styled from "styled-components"; //eslint-disable-line
import { css } from "styled-components/macro"; //eslint-disable-line
import { ReactComponent as HeartIcon } from "feather-icons/dist/icons/heart.svg";
import { ReactComponent as CheckCircleIcon } from "feather-icons/dist/icons/check-circle.svg";
import { ReactComponent as UsersIcon } from "feather-icons/dist/icons/users.svg";
import { ReactComponent as SvgDecoratorBlob } from "images/svg-decorator-blob-3.svg";

import MainFeature1 from "components/features/TwoColWithButton.js";
import TeamCardGrid from "components/cards/ProfileThreeColGrid.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import BlogCta from "blog/components/BlogCta.js";
import { HighlightedText, Eyebrow } from "blog/components/ui.js";
import usePageMeta from "blog/utils/usePageMeta.js";

/**
 * Page « Qui sommes-nous ? » — IA Besançon est un journal indépendant, à but non lucratif et à mission :
 * mettre à découvert les technologies émergentes du bassin de Besançon, par passion et pour la transmettre.
 */

const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Credit = tw.small`block mt-4 text-xs text-gray-500 font-medium`;
const imageCss = tw`rounded-4xl`;

// Photos réelles : Besançon (Wikimedia Commons, CC BY-SA 4.0) et un plateau de laboratoire (Unsplash).
const BESANCON_PHOTO =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f0/Besan%C3%A7on%2C_la_vieille_ville_vue_depuis_les_Glacis.jpg/1280px-Besan%C3%A7on%2C_la_vieille_ville_vue_depuis_les_Glacis.jpg";
const LAB_PHOTO = "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1200&q=80";

/* Section « Ce qui nous guide » */
const ValuesHeading = tw.div`flex flex-col items-center text-center`;
const ValuesEyebrow = tw(Eyebrow)`block mb-3`;
const ValuesTitle = tw(SectionHeading)``;
const ValuesDescription = tw(SectionDescription)`mx-auto text-center`;
const ValuesGrid = tw.div`mt-12 grid gap-8 md:grid-cols-3`;
const ValueCard = tw.div`flex flex-col items-center text-center bg-gray-100 rounded-lg p-8 border-2 border-gray-200`;
const ValueIcon = styled.div`
  ${tw`w-16 h-16 rounded-full bg-primary-500 text-gray-100 flex items-center justify-center`}
  svg {
    ${tw`w-8 h-8`}
  }
`;
const ValueTitle = tw.h3`mt-6 text-xl font-bold text-gray-900`;
const ValueText = tw.p`mt-3 text-sm md:text-base text-gray-700 font-medium leading-relaxed`;
const DecoratorBlob = tw(SvgDecoratorBlob)`pointer-events-none -z-10 absolute left-0 bottom-0 w-64 h-64 opacity-15 transform -translate-x-1/2 translate-y-1/2 text-primary-500`;

const values = [
  {
    icon: HeartIcon,
    title: "Passion",
    text:
      "Nous faisons ce journal sur notre temps libre, parce que le sujet nous passionne. Pas de publicité, pas de contenu sponsorisé : un journal à but non lucratif, qui ne vend rien.",
  },
  {
    icon: CheckCircleIcon,
    title: "Rigueur",
    text:
      "Chaque article est sourcé et référencé, avec ses liens. Quand nous ne sommes pas sûrs d'un fait, nous le disons — ou nous ne l'écrivons pas.",
  },
  {
    icon: UsersIcon,
    title: "Transmission",
    text:
      "Expliquer simplement, sans simplifier à outrance, pour que la tech d'ici profite au plus grand nombre — à commencer par celles et ceux qui débutent.",
  },
];

export default () => {
  usePageMeta({
    title: "Qui sommes-nous ?",
    description:
      "IA Besançon est un journal indépendant et à but non lucratif, à mission : mettre à découvert les technologies émergentes du bassin de Besançon, par passion et pour la transmettre.",
    path: "/about",
  });

  return (
    <AnimationRevealPage>
      <MainFeature1
        subheading={<Subheading>Qui sommes-nous ?</Subheading>}
        heading={
          <>
            Un journal indépendant, <HighlightedText>né d'une passion.</HighlightedText>
          </>
        }
        description={
          <>
            IA Besançon est un journal à but non lucratif consacré à l'intelligence artificielle, à la tech et à l'innovation dans le bassin de
            Besançon. Nous ne vendons rien : nous observons, nous nous renseignons et nous racontons, par passion, ce qui se construit ici — dans
            les laboratoires, les startups, les écoles et les collectivités. Le but est simple : transmettre cette passion et la rendre utile à
            d'autres.
            <Credit>Photo : Besançon, la vieille ville vue depuis les Glacis — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons</Credit>
          </>
        }
        buttonRounded={false}
        primaryButtonText="Lire le journal"
        primaryButtonUrl="/blog"
        primaryButtonTarget="_self"
        headingAs="h1"
        imageSrc={BESANCON_PHOTO}
        imageCss={imageCss}
        imageDecoratorBlob={true}
      />

      <MainFeature1
        subheading={<Subheading>Notre mission</Subheading>}
        heading={
          <>
            Mettre à découvert les technologies émergentes de <HighlightedText>notre belle région.</HighlightedText>
          </>
        }
        description={
          <>
            Besançon et son bassin regorgent d'innovations discrètes : microtechniques, capteurs, santé, énergie, intelligence artificielle. Elles
            restent souvent invisibles hors des cercles spécialisés. Nous allons les chercher, nous les expliquons simplement et nous citons nos
            sources, pour que chacun — étudiant, curieux, entrepreneur, élu — puisse comprendre ce qui se joue ici et s'en emparer.
          </>
        }
        buttonRounded={false}
        primaryButtonText="Proposer un sujet"
        primaryButtonUrl="/contact"
        primaryButtonTarget="_self"
        imageSrc={LAB_PHOTO}
        imageCss={imageCss}
        imageDecoratorBlob={true}
        textOnLeft={false}
      />

      <Container>
        <ContentWithPaddingXl>
          <ValuesHeading>
            <ValuesEyebrow>Ce qui nous guide</ValuesEyebrow>
            <ValuesTitle>
              Un journal <HighlightedText>à mission.</HighlightedText>
            </ValuesTitle>
            <ValuesDescription>
              Trois engagements, valables pour chaque article que nous publions.
            </ValuesDescription>
          </ValuesHeading>
          <ValuesGrid>
            {values.map(value => (
              <ValueCard key={value.title}>
                <ValueIcon aria-hidden="true">
                  <value.icon />
                </ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueText>{value.text}</ValueText>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ContentWithPaddingXl>
        <DecoratorBlob aria-hidden="true" />
      </Container>

      <TeamCardGrid
        subheading={<Subheading>Derrière le journal</Subheading>}
        heading="Qui écrit ?"
        description={
          <>
            Jimmy Inthalangsy, développeur IA et fondateur du journal, enquête, écrit et vérifie. Le journal est ouvert aux contributions : un
            sujet, un témoignage, une visite de laboratoire ou d'atelier — écrivez-nous.
          </>
        }
      />

      <BlogCta
        heading="Vous connaissez une innovation qui mérite d'être racontée ?"
        text="Laboratoire, startup, atelier, école, collectivité : parlez-nous-en. Nous venons voir, nous posons des questions, nous racontons — gratuitement et sans contrepartie."
        primaryText="Nous écrire"
        primaryTo="/contact"
        secondaryText="Lire le journal"
        secondaryTo="/blog"
      />
    </AnimationRevealPage>
  );
};
